import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import useService from "../../../../hooks/useService";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";
import { useSettlementStore } from "../store/settlementStore";

import useSettlementPermissions from "./useSettlementPermissions";

export default function useSettlementCaseForm({
  caseDetail,
  buildValues,
  buildPayload,
  isDirtyCheck,
}) {
  const { id } = useParams();
  const caseRepository = useService("repositories/case-repository");
  const { openFailureModal } = getModalHandler();
  const { canEditCaseSettlement } = useSettlementPermissions();
  const setCaseDetail = useSettlementStore((state) => state.setCaseDetail);
  const bumpRefreshVersion = useSettlementStore((state) => state.bumpRefreshVersion);
  const initialValues = useMemo(() => buildValues(caseDetail), [buildValues, caseDetail]);
  const [formValues, setFormValues] = useState(initialValues);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const isDirty = useMemo(
    () => isDirtyCheck(formValues, initialValues),
    [formValues, initialValues, isDirtyCheck]
  );

  useEffect(() => {
    if (!isEditing && !isSaving) {
      setFormValues(initialValues);
    }
  }, [initialValues, isEditing, isSaving]);

  const handleCancel = () => {
    setFormValues(initialValues);
    setIsEditing(false);
  };

  const handleSave = async (onSuccess, onError) => {
    if (!id || !isDirty || !canEditCaseSettlement) {
      return;
    }

    setIsSaving(true);

    try {
      await caseRepository.patch(id, buildPayload(formValues));
      const refreshedCase = await caseRepository.findRecord(id, { noCache: true });
      setCaseDetail(refreshedCase);
      bumpRefreshVersion();
      setIsEditing(false);
      onSuccess?.();
    } catch (error) {
      setIsEditing(true);
      onError?.(error);
      openFailureModal(error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    formValues,
    setFormValues,
    initialValues,
    isDirty,
    isEditing,
    setIsEditing,
    isSaving,
    canEditCaseSettlement,
    handleCancel,
    handleSave,
  };
}
